import React, { useEffect, useState, useCallback } from 'react';
import {
    Container, Typography, Box, Card, CardContent,
    CircularProgress, Alert, Grid, Divider, Chip, Tooltip,
    TextField, Button, List, ListItem, ListItemButton, ListItemText, Paper
} from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import SearchIcon from '@mui/icons-material/Search';

const PronosticoTiempo = () => {
    const [current, setCurrent] = useState(null);
    const [hourly, setHourly] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ciudad, setCiudad] = useState('');
    const [sugerencias, setSugerencias] = useState([]);
    const [ciudadSeleccionada, setCiudadSeleccionada] = useState(null);
    const [buscando, setBuscando] = useState(false);

    const buscarCiudad = async () => {
        if (!ciudad.trim()) return;
        setBuscando(true);
        setSugerencias([]);
        try {
            const res = await fetch(
                `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(ciudad)}&count=6&language=es&format=json`
            );
            const data = await res.json();
            setSugerencias(data.results || []);
        } catch {
            setSugerencias([]);
        } finally {
            setBuscando(false);
        }
    };

    const fetchClima = useCallback(async (lat, lon, tz, nombre) => {
        setLoading(true);
        setError(null);
        setCurrent(null);
        setHourly([]);
        setSugerencias([]);
        try {
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,wind_speed_10m,relative_humidity_2m,weather_code&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&timezone=${encodeURIComponent(tz)}&forecast_days=2`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('No se pudo obtener el pronóstico');
            const data = await res.json();

            setCurrent({
                time: data.current.time,
                temperature: data.current.temperature_2m,
                wind: data.current.wind_speed_10m,
                humidity: data.current.relative_humidity_2m,
                code: data.current.weather_code,
                nombre,
            });

            const ahora = new Date(data.current.time);
            const entradas = data.hourly.time
                .map((t, i) => ({
                    time: t,
                    temperature: data.hourly.temperature_2m[i],
                    humidity: data.hourly.relative_humidity_2m[i],
                    wind: data.hourly.wind_speed_10m[i],
                    code: data.hourly.weather_code[i],
                }))
                .filter(e => new Date(e.time) >= ahora)
                .slice(0, 24);

            setHourly(entradas);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar Medellín por defecto al montar
    useEffect(() => {
        fetchClima(6.2442, -75.5812, 'America/Bogota', 'Medellín, Colombia');
    }, [fetchClima]);

    const weatherLabel = (code) => {
        if (code === 0) return { label: 'Despejado', icon: '☀️' };
        if (code <= 3) return { label: 'Parcialmente nublado', icon: '⛅' };
        if (code <= 9) return { label: 'Neblina', icon: '🌫️' };
        if (code <= 19) return { label: 'Lluvia ligera', icon: '🌦️' };
        if (code <= 29) return { label: 'Tormenta', icon: '⛈️' };
        if (code <= 39) return { label: 'Nieve', icon: '❄️' };
        if (code <= 49) return { label: 'Neblina', icon: '🌫️' };
        if (code <= 59) return { label: 'Llovizna', icon: '🌧️' };
        if (code <= 69) return { label: 'Lluvia', icon: '🌧️' };
        if (code <= 79) return { label: 'Nieve', icon: '❄️' };
        if (code <= 84) return { label: 'Aguacero', icon: '🌧️' };
        if (code <= 99) return { label: 'Tormenta', icon: '⛈️' };
        return { label: 'Desconocido', icon: '🌡️' };
    };

    const formatHora = (isoStr) =>
        new Date(isoStr).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

    const formatFecha = (isoStr) =>
        new Date(isoStr).toLocaleDateString('es-CO', {
            weekday: 'long', day: 'numeric', month: 'long'
        });

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                🌤 Pronóstico del tiempo
            </Typography>

            {/* Formulario de búsqueda */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    label="Buscar ciudad"
                    value={ciudad}
                    onChange={e => setCiudad(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscarCiudad()}
                    sx={{ minWidth: 240 }}
                    placeholder="Ej: Bogotá, Madrid, New York..."
                />
                <Button
                    variant="contained"
                    onClick={buscarCiudad}
                    disabled={buscando || !ciudad.trim()}
                    startIcon={<SearchIcon />}
                >
                    {buscando ? 'Buscando...' : 'Buscar'}
                </Button>
            </Box>

            {/* Lista de sugerencias */}
            {sugerencias.length > 0 && (
                <Paper elevation={3} sx={{ mb: 2, maxWidth: 400 }}>
                    <List dense disablePadding>
                        {sugerencias.map((s, i) => (
                            <ListItem key={i} disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        const nombre = `${s.name}${s.admin1 ? ', ' + s.admin1 : ''}, ${s.country}`;
                                        setCiudadSeleccionada(nombre);
                                        setCiudad(nombre);
                                        fetchClima(s.latitude, s.longitude, s.timezone || 'auto', nombre);
                                    }}
                                >
                                    <ListItemText
                                        primary={s.name}
                                        secondary={`${s.admin1 ? s.admin1 + ' · ' : ''}${s.country} (${s.latitude.toFixed(2)}, ${s.longitude.toFixed(2)})`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {current && !loading && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {current.nombre} · {formatFecha(current.time)}
                    </Typography>

                    {/* Tarjeta actual */}
                    <Card elevation={4} sx={{ mb: 4, background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)', color: '#fff' }}>
                        <CardContent>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 64 }}>{weatherLabel(current.code).icon}</Typography>
                                    <Typography variant="h6">{weatherLabel(current.code).label}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        Actualizado: {formatHora(current.time)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ThermostatIcon />
                                                <Box>
                                                    <Typography variant="h4" fontWeight="bold">{current.temperature}°C</Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Temperatura</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AirIcon />
                                                <Box>
                                                    <Typography variant="h4" fontWeight="bold">{current.wind} km/h</Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Viento</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WaterDropIcon />
                                                <Box>
                                                    <Typography variant="h4" fontWeight="bold">{current.humidity}%</Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Humedad</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Próximas 24 horas */}
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        <WbSunnyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Próximas 24 horas
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 2 }}>
                        {hourly.map((h, i) => {
                            const { icon, label } = weatherLabel(h.code);
                            return (
                                <Tooltip key={i} title={label} arrow>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            minWidth: 90,
                                            textAlign: 'center',
                                            p: 1,
                                            flexShrink: 0,
                                            bgcolor: i === 0 ? '#e3f2fd' : 'background.paper',
                                            border: i === 0 ? '2px solid #1565c0' : '1px solid #e0e0e0'
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {formatHora(h.time)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 28, lineHeight: 1.3 }}>{icon}</Typography>
                                        <Typography variant="body2" fontWeight="bold">{h.temperature}°C</Typography>
                                        <Chip
                                            icon={<WaterDropIcon sx={{ fontSize: '12px !important' }} />}
                                            label={`${h.humidity}%`}
                                            size="small"
                                            sx={{ mt: 0.5, fontSize: 10, height: 20 }}
                                        />
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {h.wind} km/h
                                        </Typography>
                                    </Card>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </>
            )}
        </Container>
    );
};

export default PronosticoTiempo;
